import { getWebAppUrl } from '@/services/admin_actions/apiUtils';
import { authService } from '@/services/authService';
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { useUserStore } from '@/store/userJWTStore'; // Import the user store to check authentication status
import { useBottomSheetStore } from '@/store/bottomSheetStore';
import MembershipInfoPopUp from '@/components/MobileAppComponents/MembershipInfoPopUp.vue';

const webRoutes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/:lang(en|fr|ar|es)?', // Added to handle optional lang prefix
    redirect: (to: { params: { lang?: string } }) => {
      const lang = to.params.lang ? `/${to.params.lang}` : '';
      return `${lang}/dashboard`;
    },
    component: () => import('@/layouts/DefaultLayout.vue'),
    meta: { RequiresAuth: true, LogUserOut: false },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/SitePages/Dashboard.vue'),
        meta: { hideCommunitiesWidget: true, LogUserOut: false }, // Add this line to hide the communities widget
      },
      {
        path: 'app_login',
        name: 'Login',
        component: () => import('@/views/SitePages/Login.vue'),
        meta: { RequiresAuth: false, LogUserOut: true },
      },
      {
        path: 'menu_login',
        name: 'Login',
        meta: { RequiresAuth: false, LogUserOut: true },
        component: () => (isMobileApp() ? import('@/views/SitePages/Login.vue') : import('@/views/AdminPages/DynamicContent.vue')),
      },
      {
        path: 'skillstesting',
        name: 'Skills Testing',
        component: () => import('@/views/SitePages/SkillsTesting.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'players',
        name: 'Player Ratings',
        component: () => import('@/views/SitePages/Players.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'info',
        name: 'Info',
        component: () => import('@/views/AdminPages/Info.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'americano',
        name: 'Americano',
        component: () => import('@/views/SitePages/Americano.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'boxes',
        name: 'Boxes',
        component: () => import('@/views/SitePages/Boxes.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'box',
        name: 'Box League',
        component: () => import('@/views/SitePages/BoxDetail.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'court_analysis',
        name: 'Court Analysis',
        component: () => import('@/views/SitePages/CourtAnalysis.vue'),
        meta: { RequiresAuth: true, LogUserOut: false },
      },
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/SitePages/Search.vue'),
        meta: { hideSearchWidget: true, LogUserOut: false }, // Add this line to hide the search widget
      },
      {
        path: 'invites',
        name: 'Invites',
        component: () => import('@/views/SitePages/Invites.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'add_result',
        name: 'Add Result',
        component: () => import('@/views/SitePages/AddResult.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'add_new_community',
        name: 'Add New Community',
        component: () => import('@/views/ConfigurationPages/Add_New_Community.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'what_if',
        name: 'What If',
        component: () => import('@/views/SitePages/WhatIf.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'calendar',
        name: 'Calendar',
        component: () => import('@/views/SitePages/Calendar.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'calendar_submit',
        name: 'Calendar Submit',
        component: () => import('@/views/SitePages/CalendarSubmit.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'find_a_player',
        name: 'Find A Player',
        component: () => import('@/views/SitePages/FindPlayer.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: ':catchAll(.*)',
        name: 'Dynamic Content',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { LogUserOut: false },
      },
      {
        path: 'subscribe',
        name: 'Subscribe',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { WebRestricted: true, LogUserOut: false }, // This route is restricted to the web version only
      },
      {
        path: 'membership',
        name: 'Membership',
        component: () => import('@/views/AdminPages/DynamicContent.vue'),
        meta: { WebRestricted: true, LogUserOut: false }, // This route is restricted to the web version only
      },
      {
        path: 'debug_app',
        name: 'Debug App',
        component: () => import('@/components/TestingComponents/UserDebugPage.vue'),
      },
    ],
  },
  {
    path: '/print',
    component: () => import('@/layouts/PrintLayout.vue'),
    meta: { RequiresAuth: false, LogUserOut: false },
    children: [
      {
        path: 'box_print',
        name: 'Boxes Print',
        component: () => import('@/views/SitePages/BoxPrint.vue'),
        meta: { LogUserOut: false },
      },
    ],
  },
];

const PerformWebRules = async (to: any, from: any, next: any) => {
  const userStore = useUserStore();
  const isApp = isMobileApp();
  
  if (isApp && userStore.initialized && userStore.isLoggedOut && to.meta.RequiresAuth) {
    return next('/app_login');
  }
  
  if (isApp && to.meta.WebRestricted) {
    const sheet = useBottomSheetStore();
    sheet.openSheet({
      id: 'membership-info',
      component: MembershipInfoPopUp,
      options: {
        bgColor: 'primary',
        siteLogo: true,
        showOnDesktop: true,
        disablePersistence: true, // 👈 correct place
      },
    });
    return next(false);
  }
};

const WebAfterRules = async (to: any, from: any) => {
  const userStore = useUserStore();
  const isApp = isMobileApp();
  
  if (isApp && to.meta.LogUserOut) {
    await authService.logout();
  }
};

export { PerformWebRules, WebAfterRules, webRoutes };
